import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { startOfMonth } from "date-fns";
import { calendarService, queryKeys, roomsService } from "@/services";
import type {
  AvailabilityCalendarEvent,
  CalendarEventManage,
  CalendarEventPublic,
  RoomManage,
  RoomPublic,
} from "@/types";
import {
  getAdjacentMonthKeys,
  getMonthQueryRange,
} from "@/utils/calendarMonthRange";

interface UseAvailabilityCalendarOptions {
  mode: "public" | "manage";
  tenantSlug: string;
  roomId?: string;
}

interface UseAvailabilityCalendarResult {
  rooms: Array<RoomPublic | RoomManage>;
  selectedRoomId: string | null;
  setSelectedRoomId: (roomId: string) => void;
  weekStart: Date;
  setWeekStart: (date: Date) => void;
  events: AvailabilityCalendarEvent[];
  manageEvents: CalendarEventManage[];
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

const mapPublicEvents = (
  events: CalendarEventPublic[],
  roomId: string,
): AvailabilityCalendarEvent[] =>
  events
    .filter((event) => event.roomIds.includes(roomId))
    .map((event) => ({
      id: event.id,
      sourceEventId: event.id,
      startsAt: event.startsAt,
      endsAt: event.endsAt,
      title: event.title,
      visibility: event.visibility,
      displayScope:
        event.displayKind === "pending"
          ? ("pending" as const)
          : ("room" as const),
    }));

const mapManageEvents = (
  events: CalendarEventManage[],
  roomId: string,
): AvailabilityCalendarEvent[] => {
  const visualEvents: AvailabilityCalendarEvent[] = [];

  for (const event of events) {
    if (event.bookingStatus === "REJECTED") {
      continue;
    }

    const hasRoom = event.rooms.some((room) => room.id === roomId);
    const isInternal = event.visibility === "INTERNAL";

    if (hasRoom) {
      visualEvents.push({
        id: `${event.id}:room`,
        sourceEventId: event.id,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        title: event.title,
        visibility: event.visibility,
        displayScope: "room",
      });
    }

    if (isInternal) {
      visualEvents.push({
        id: `${event.id}:internal`,
        sourceEventId: event.id,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        title: event.title,
        visibility: event.visibility,
        displayScope: "internal",
      });
    }
  }

  return visualEvents;
};

const dedupeManageEvents = (
  events: CalendarEventManage[],
): CalendarEventManage[] => {
  const byId = new Map<string, CalendarEventManage>();

  for (const event of events) {
    byId.set(event.id, event);
  }

  return [...byId.values()];
};

/**
 * Loads rooms and calendar events for AvailabilityCalendar with monthly cache
 */
export const useAvailabilityCalendar = ({
  mode,
  tenantSlug,
  roomId,
}: UseAvailabilityCalendarOptions): UseAvailabilityCalendarResult => {
  const queryClient = useQueryClient();
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(
    roomId ?? null,
  );
  const [weekStart, setWeekStart] = useState(() => startOfMonth(new Date()));

  const roomsQuery = useQuery({
    queryKey:
      mode === "public"
        ? queryKeys.rooms.public(tenantSlug)
        : queryKeys.rooms.mine(tenantSlug),
    queryFn: () =>
      mode === "public"
        ? roomsService.getPublicBySlug(tenantSlug)
        : roomsService.getMine(),
    enabled: Boolean(tenantSlug),
  });

  const rooms = useMemo(() => roomsQuery.data ?? [], [roomsQuery.data]);

  useEffect(() => {
    if (roomId) {
      setSelectedRoomId(roomId);
      return;
    }

    if (selectedRoomId) {
      return;
    }

    const defaultRoomId =
      rooms.find((room) => ("isActive" in room ? room.isActive : true))?.id ??
      rooms[0]?.id ??
      null;

    if (defaultRoomId) {
      setSelectedRoomId(defaultRoomId);
    }
  }, [roomId, rooms, selectedRoomId]);

  const monthKeys = useMemo(
    () => getAdjacentMonthKeys(weekStart),
    [weekStart],
  );

  const eventQueries = useQueries({
    queries: monthKeys.map((monthKey) => {
      const range = getMonthQueryRange(monthKey);

      return {
        queryKey:
          mode === "public"
            ? queryKeys.calendar.public(
                tenantSlug,
                selectedRoomId ?? "",
                monthKey,
              )
            : queryKeys.calendar.mine(
                tenantSlug,
                selectedRoomId ?? "",
                monthKey,
              ),
        queryFn: () =>
          mode === "public"
            ? calendarService.getPublicBySlug(tenantSlug, {
                ...range,
                roomId: selectedRoomId!,
              })
            : calendarService.getMine({
                ...range,
                roomId: selectedRoomId!,
              }),
        enabled: Boolean(tenantSlug && selectedRoomId),
      };
    }),
  });

  const rawEvents = useMemo(() => {
    if (!selectedRoomId) {
      return [];
    }

    if (mode === "manage") {
      const merged = eventQueries.flatMap(
        (query) => (query.data as CalendarEventManage[] | undefined) ?? [],
      );

      return dedupeManageEvents(merged);
    }

    const merged = eventQueries.flatMap(
      (query) => (query.data as CalendarEventPublic[] | undefined) ?? [],
    );

    const byId = new Map<string, CalendarEventPublic>();

    for (const event of merged) {
      byId.set(event.id, event);
    }

    return [...byId.values()];
  }, [eventQueries, mode, selectedRoomId]);

  const events = useMemo(() => {
    if (!selectedRoomId) {
      return [];
    }

    return mode === "public"
      ? mapPublicEvents(rawEvents as CalendarEventPublic[], selectedRoomId)
      : mapManageEvents(rawEvents as CalendarEventManage[], selectedRoomId);
  }, [mode, rawEvents, selectedRoomId]);

  const manageEvents =
    mode === "manage" ? (rawEvents as CalendarEventManage[]) : [];

  const hasCachedEvents = eventQueries.some((query) => query.data !== undefined);

  const loading =
    (roomsQuery.isLoading && rooms.length === 0) ||
    (Boolean(selectedRoomId) &&
      !hasCachedEvents &&
      eventQueries.some((query) => query.isLoading));

  const queryError =
    roomsQuery.error ??
    eventQueries.find((query) => query.error)?.error ??
    null;

  const error = queryError instanceof Error ? queryError : null;

  const reload = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey:
          mode === "public"
            ? queryKeys.rooms.public(tenantSlug)
            : queryKeys.rooms.mine(tenantSlug),
      }),
      queryClient.invalidateQueries({
        queryKey:
          mode === "public"
            ? queryKeys.calendar.publicAll(tenantSlug)
            : queryKeys.calendar.mineAll(tenantSlug),
      }),
    ]);
  }, [mode, queryClient, tenantSlug]);

  return {
    rooms,
    selectedRoomId,
    setSelectedRoomId,
    weekStart,
    setWeekStart,
    events,
    manageEvents,
    loading,
    error,
    reload,
  };
};
